<template>
  <div class="tax-injector-wrapper p-4">
    <!-- PageBreadcrumb Navigation -->
    <div class="mb-4">
      <PageBreadcrumb />
    </div>

    <!-- Usage Instructions Card -->
    <div
      class="bg-surface-0 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-lg shadow-sm p-6 mb-6"
    >
      <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-0 mb-3">
        Usage Instructions
      </h3>
      <p class="text-surface-700 dark:text-surface-200">
        Click <b>Select File</b> to select the file to upload to the Tax
        Injector, then click <b>Upload</b>.<br />
        Only CSV files up to 5MB are accepted.
      </p>
    </div>

    <!-- File Upload Section -->
    <div class="card">
      <FileUpload
        ref="fileUploadRef"
        name="file"
        :url="apiUrl"
        @error="onUploadError"
        :multiple="false"
        accept="text/csv"
        :maxFileSize="5242880"
        :invalidFileSizeMessage="`File size must be less than 5MB.`"
        :invalidFileTypeMessage="`Only CSV files are allowed.`"
        @select="onSelectedFiles"
        @progress="onUploadProgress"
        @dragenter="clearFileUploadErrors"
        @dragover="clearFileUploadErrors"
        @drop="clearFileUploadErrors"
        @clear="onClear"
        :custom-upload="true"
        :auto="false"
      >
        <template #header="{ chooseCallback, files: headerFiles }">
          <div class="flex flex-wrap justify-between items-center flex-1 gap-4">
            <div class="flex gap-2">
              <Button
                @click="chooseCallback()"
                icon="pi pi-file-plus"
                label="Select File"
                rounded
                outlined
                severity="primary"
                :disabled="files && files.length > 0"
              ></Button>
              <Button
                @click="doCustomUpload(headerFiles)"
                icon="pi pi-upload"
                label="Upload"
                rounded
                :disabled="!headerFiles || headerFiles.length === 0 || isUploading"
                :loading="isUploading"
                severity="success"
                data-test-id="upload-button"
              ></Button>
            </div>
            <ProgressBar
              :value="totalSizePercent"
              :showValue="false"
              class="md:w-20rem h-1 w-full md:ml-auto"
            >
              <span class="whitespace-nowrap">{{ totalSize }}B / 1Mb</span>
            </ProgressBar>
          </div>
        </template>
        <template
          #content="{ files, uploadedFiles, removeFileCallback, messages }"
        >
          <div class="flex flex-col gap-8 pt-4">
            <Message
              v-for="message of messages"
              :key="message"
              :class="{ 'mb-8': !files.length && !uploadedFiles.length }"
              severity="error"
            >
              {{ message }}
            </Message>

            <div v-if="files.length > 0">
              <h5
                class="text-xl font-bold text-surface-900 dark:text-surface-0 mb-3 flex items-center gap-2"
              >
                <i class="pi pi-clock text-yellow-500 dark:text-yellow-400"></i>Pending
              </h5>
              <div class="flex flex-wrap gap-4">
                <div
                  v-for="(file, index) of files"
                  :key="file.name + file.type + file.size"
                  class="p-8 rounded-border flex flex-col border border-surface items-center gap-4"
                >
                  <span
                    class="font-semibold text-ellipsis max-w-60 whitespace-nowrap overflow-hidden"
                    >{{ file.name }}</span
                  >
                  <Button
                    icon="pi pi-times"
                    label="Cancel"
                    @click="
                      onRemoveTemplatingFile(file, removeFileCallback, index)
                    "
                    outlined
                    rounded
                    severity="danger"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </FileUpload>
    </div>

    <!-- Validation Errors Section (only shown on upload failure) -->
    <div v-if="validationError" class="card mt-4" data-test-id="validation-errors">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-xl font-bold text-red-600 dark:text-red-400">
          <i class="pi pi-exclamation-triangle mr-2"></i>Upload Validation Errors
        </h3>
        <Button
          icon="pi pi-times"
          severity="secondary"
          text
          rounded
          @click="clearValidationError"
          aria-label="Dismiss errors"
        />
      </div>
      <Message severity="error" :closable="false">
        <div class="flex flex-col gap-2">
          <div class="font-bold">{{ validationError.Message }}</div>
          <div class="mt-2 p-3 bg-surface-50 dark:bg-surface-800 rounded overflow-x-auto max-h-60 overflow-y-auto">
            <div
              v-for="rowError in validationError.CsvRowErrors"
              :key="rowError.RowNumber"
              class="p-2 border-l-2 border-red-300 dark:border-red-700 pl-3 mb-2"
            >
              <span class="font-semibold text-surface-900 dark:text-surface-0">Row {{ rowError.RowNumber }}:</span>
              <ul class="m-0 pl-4 pt-1">
                <li
                  v-for="(fieldError, idx) in rowError.FieldErrors"
                  :key="`${rowError.RowNumber}-${idx}`"
                  class="text-surface-700 dark:text-surface-200"
                >
                  Field
                  <span class="font-mono bg-surface-100 dark:bg-surface-700 px-1 rounded">{{
                    fieldError.Field
                  }}</span>: {{ fieldError.Message }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Message>
    </div>

    <!-- Upload History Table -->
    <div class="mt-4">
      <div v-if="canViewAllInjections" class="flex items-center gap-3 mb-3">
        <ToggleSwitch v-model="isAdminMode" data-test-id="admin-mode-toggle" inputId="admin-mode-toggle" />
        <label for="admin-mode-toggle" class="text-sm font-medium text-surface-700 dark:text-surface-200 cursor-pointer select-none">
          View all users' injections
        </label>
      </div>
      <TaxInjectorHistoryTable
        ref="historyTableRef"
        :injectors="store.injectors"
        :loading="store.isLoadingInjectors"
        :is-admin-mode="isAdminMode"
        :total-elements="store.totalElements"
        :current-page="store.currentPage"
        :page-size="store.pageSize"
        @row-select="handleRowSelect"
        @refresh="handleHistoryRefresh"
        @download-file="handleDownloadFile"
        @filter-change="onEmailFilterChange"
        @page-change="onPageChange"
        @sort-change="onSortChange"
      />
    </div>

    <!-- Tax Injector Drawer -->
    <TaxInjectorDrawer
      v-model:visible="isDrawerVisible"
      :external-id="selectedExternalId"
      @download-file="handleDownloadFile"
    />
  </div>
</template>

<script setup lang="ts">
import { Button, FileUpload, Message, ProgressBar, ToggleSwitch } from 'primevue'
import { useFrontegg } from '@frontegg/vue'
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePrimeVue } from 'primevue/config'
import { useToast } from 'primevue/usetoast'
import { successToast, warnToast, errorToastWithDetails } from '@/util/toasts'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import { useTaxInjectorStore } from '@/stores/taxInjectorStore'
import { taxInjectorApi } from '@/services/taxInjectorService'
import {
  isUploadSuccess,
  isValidationError,
  type TaxInjectorValidationError,
  type TaxInjectorSummary,
  type DownloadUrlResource,
} from '@/types/taxInjector'
import TaxInjectorHistoryTable from '@/components/taxinjector/TaxInjectorHistoryTable.vue'
import TaxInjectorDrawer from '@/components/taxinjector/TaxInjectorDrawer.vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { normalizeError } from '@/util/errorNormalizer'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

// Define interfaces for the component
interface UploadFile {
  name: string
  size: number
  type: string
}

interface ProgressEvent {
  progress: number
}

const { authState } = useFrontegg()
const route = useRoute()
const router = useRouter()
const store = useTaxInjectorStore()
const { hasPerms } = usePerms()

// Admin mode toggle state
const canViewAllInjections = computed(() => hasPerms(PERMISSIONS.TAX_INJECTION_ADMIN))
const isAdminModeRaw = ref(false)
const isAdminMode = computed({
  get: () => canViewAllInjections.value && isAdminModeRaw.value,
  set: (val: boolean) => {
    isAdminModeRaw.value = val
  },
})

const emailFilter = ref('')
const currentSort = ref('audit.createdOn,desc')

let emailFilterTimer: ReturnType<typeof setTimeout> | null = null

function onEmailFilterChange(email: string) {
  emailFilter.value = email
  if (emailFilterTimer) clearTimeout(emailFilterTimer)
  emailFilterTimer = setTimeout(async () => {
    try {
      await store.fetchInjectors(authState, isAdminMode.value, email || undefined, 0, store.pageSize, currentSort.value)
    } catch (error) {
      const apiError = normalizeError(error)
      errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    }
  }, 300)
}

async function onPageChange(event: { page: number; rows: number }) {
  if (store.isLoadingInjectors) return
  const email = isAdminMode.value ? emailFilter.value || undefined : undefined
  try {
    await store.fetchInjectors(authState, isAdminMode.value, email, event.page, event.rows, currentSort.value)
  } catch (error) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}

async function onSortChange(sort: string) {
  currentSort.value = sort
  const email = isAdminMode.value ? emailFilter.value || undefined : undefined
  try {
    await store.fetchInjectors(authState, isAdminMode.value, email, 0, store.pageSize, currentSort.value)
  } catch (error) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}

const $primevue = usePrimeVue()
const toast = useToast()
const fileUploadRef = ref()
const historyTableRef = ref()

const totalSize = ref(0)
const totalSizePercent = ref(0)
const files = ref<UploadFile[]>([])
const isUploading = ref(false)
const validationError = ref<TaxInjectorValidationError | null>(null)
const apiUrl = API_ENDPOINTS.TAX_INJECTOR.UPLOAD_FILE

// Store previously uploaded filenames for duplicate detection
const uploadedFileSignatures = ref<Set<string>>(new Set())

// Drawer state - computed from route
const selectedExternalId = computed(() => {
  return (route.params.externalId as string) || null
})

const isDrawerVisible = computed({
  get: () => !!selectedExternalId.value,
  set: (value: boolean) => {
    if (!value && selectedExternalId.value) {
      // Navigate to base path without the externalId
      router.push('/tax/injector')
    }
  },
})

// Generate a unique signature for a file based on name and size
const getFileSignature = (file: UploadFile): string => {
  return `${file.name}-${file.size}`
}

// Check if a file has been previously uploaded
const isFileDuplicate = (file: UploadFile): boolean => {
  const signature = getFileSignature(file)
  return uploadedFileSignatures.value.has(signature)
}

const onSelectedFiles = (event: { files: UploadFile[] }) => {
  totalSizePercent.value = 0
  toast.removeAllGroups()

  const selectedFile = event.files[0]

  files.value = []
  totalSize.value = 0

  if (selectedFile) {
    // Check if file was previously uploaded
    if (isFileDuplicate(selectedFile)) {
      // Show warning toast
      warnToast(
        toast,
        'Possible Duplicate File',
        `A file with the name "${selectedFile.name}" has already been uploaded. Are you sure you want to upload it again?`,
      )
    }

    files.value = [selectedFile]
    totalSize.value = parseInt(formatSize(selectedFile.size))
    totalSizePercent.value = totalSize.value / 10
  }
}

const onRemoveTemplatingFile = (
  file: UploadFile,
  removeFileCallback: Function,
  index: number,
) => {
  removeFileCallback(index)
  files.value.splice(index, 1)
  totalSize.value -= parseInt(formatSize(file.size))
  totalSizePercent.value = totalSize.value / 10
}

const doCustomUpload = async (uploadFiles: any) => {
  // Prevent double-click by checking loading state
  if (isUploading.value) return

  // Clear any previous validation errors
  validationError.value = null

  // Ensure the file exists before proceeding
  if (
    !uploadFiles ||
    (Array.isArray(uploadFiles) && uploadFiles.length === 0)
  ) {
    console.warn('Upload called without a file selected')
    return
  }

  try {
    // Set loading state BEFORE any async operations to prevent double-clicks
    isUploading.value = true

    const file = Array.isArray(uploadFiles) ? uploadFiles[0] : uploadFiles

    // Set up progress tracking
    const onProgress = (progressEvent: any) => {
      totalSizePercent.value = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total,
      )
    }

    // Use the service to upload
    const response = await taxInjectorApi.uploadFile(authState, file, onProgress)

    // Check response type
    if (isUploadSuccess(response)) {
      // Handle successful response
      console.log('Upload response:', response)

      // Add the file signature to our tracking set
      const fileSignature = getFileSignature(file)
      uploadedFileSignatures.value.add(fileSignature)

      const detail = `File "${file.name}" uploaded successfully. Tax Injector ID: ${response.ExternalId}`

      successToast(toast, 'Upload Successful', detail)

      // Refresh the injectors list to show the new upload
      const email = isAdminMode.value ? emailFilter.value || undefined : undefined
      await store.fetchInjectors(authState, isAdminMode.value, email, 0, store.pageSize, currentSort.value)

      // Clear the file from the component
      fileUploadRef.value.clear()
    } else if (isValidationError(response)) {
      // This shouldn't happen since validation errors are thrown, but handle just in case
      validationError.value = response
      onUploadError()
    }
  } catch (error: unknown) {
    console.error('Upload failed:', error)
    const apiError = normalizeError(error)

    // Detect validation error: ApiError has code (lowercase) and CsvRowErrors in originalResponse
    const isValidation =
      apiError.code === 'VALIDATION_ERROR' &&
      apiError.originalResponse &&
      typeof apiError.originalResponse === 'object' &&
      'CsvRowErrors' in apiError.originalResponse &&
      Array.isArray((apiError.originalResponse as TaxInjectorValidationError).CsvRowErrors)

    if (isValidation) {
      validationError.value = apiError.originalResponse as TaxInjectorValidationError
      errorToastWithDetails(
        toast,
        'Validation Failed',
        apiError.toToastMessage(),
        apiError,
      )
    } else {
      errorToastWithDetails(
        toast,
        'Upload Failed',
        apiError.toToastMessage(),
        apiError,
      )
    }

    onUploadError()
  } finally {
    // Always reset loading state, even on error
    isUploading.value = false
  }
}

const onUploadProgress = (event: ProgressEvent) => {
  totalSizePercent.value = event.progress
}

const clearFileUploadErrors = () => {
  if (fileUploadRef.value) {
    fileUploadRef.value.clear()
    toast.removeAllGroups()
  }
}

const onUploadError = () => {
  totalSizePercent.value = 0
}

const onClear = () => {
  files.value = []
  totalSize.value = 0
  totalSizePercent.value = 0
}

const clearValidationError = () => {
  validationError.value = null
}

const formatSize = (bytes: number): string => {
  const k = 1024
  const dm = 3
  const sizes = $primevue.config.locale?.fileSizeTypes || [
    'B',
    'KB',
    'MB',
    'GB',
    'TB',
  ]

  if (bytes === 0) {
    return `0 ${sizes[0]}`
  }

  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm))

  return `${formattedSize} ${sizes[i]}`
}

// Handle row selection from history table
const handleRowSelect = (injector: TaxInjectorSummary) => {
  store.setSelectedInjector(injector)
  router.push({
    name: 'taxInjector',
    params: { externalId: injector.ExternalId },
  })
}

// Handle refresh of history table
const handleHistoryRefresh = async () => {
  const email = isAdminMode.value ? emailFilter.value || undefined : undefined
  try {
    await store.fetchInjectors(authState, isAdminMode.value, email, 0, store.pageSize, currentSort.value)
  } catch (error) {
    console.error('Failed to refresh injectors:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}

// Handle download file request
const handleDownloadFile = async (injector: TaxInjectorSummary) => {
  try {
    // Get download URL from API
    const downloadResponse: DownloadUrlResource = await store.getDownloadUrl(authState, injector.ExternalId)
    
    // Create download link and trigger automatically
    const link = document.createElement('a')
    link.href = downloadResponse.downloadUrl
    link.download = `tax-injector-${injector.ExternalId}.csv`
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Show success toast
    successToast(toast, 'Download Started', 'Your file download has begun')
    
  } catch (error: any) {
    console.error('Download failed:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Download Failed', apiError.toToastMessage(), apiError)
  } finally {
    // Clear loading states in child components
    if (historyTableRef.value) {
      historyTableRef.value.clearDownloadingState(injector.ExternalId)
    }
  }
}

// Refetch injectors when admin mode toggle changes
watch(isAdminMode, async (newValue) => {
  if (!newValue) {
    emailFilter.value = ''
    if (emailFilterTimer) clearTimeout(emailFilterTimer)
  }
  const email = newValue ? emailFilter.value || undefined : undefined
  try {
    await store.fetchInjectors(authState, newValue, email, 0, store.pageSize, currentSort.value)
  } catch (error) {
    console.error('Failed to fetch injectors in admin mode:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
})

// Fetch injectors on mount
onMounted(async () => {
  const email = isAdminMode.value ? emailFilter.value || undefined : undefined
  try {
    await store.fetchInjectors(authState, isAdminMode.value, email, 0, store.pageSize, currentSort.value)
  } catch (error) {
    console.error('Failed to fetch injectors:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
})

// Handle direct URL access to drawer
watch(
  () => route.params.externalId,
  async (externalId) => {
    if (externalId && typeof externalId === 'string') {
      let injector = store.findInjectorById(externalId)
      if (!injector) {
        // Not on the current page — fetch the single injector directly
        try {
          injector = await store.fetchInjectorById(authState, externalId)
        } catch {
          // injector not found or access denied — drawer will handle empty state
        }
      }
      if (injector) {
        store.setSelectedInjector(injector)
      }
    }
  },
  { immediate: true },
)
</script>
